<template>
  <div class="comment-list-item" :style="cssClass">
    <div class="comment-header">
      <div class="comment-metadata">
        <div class="comment-avatar-name">
          <user-avatar
            v-if="!isCommentCreatedByScorableApp"
            :user-avatar-url="commentedByUserAvatarFile"
            :user-id="commentedByUserId"
          />
          <ellipsis-text
            class="name"
            :class="{
              'learning-fa': isCommentCreatedByIntegratorApp && !isCommentCreatedByScorableApp,
            }"
            :text="commentedBy"
          />
        </div>
        <div class="info">
          <span v-if="(edited || hasEdit) && !comment.deleted" class="edited">{{
            t("edited_label")
          }}</span>
          <span class="date">{{ commentDateString }}</span>
          <span v-if="showFlaggedMessage" class="flagged-text">
            <svg-icon name="Warning16" kind="indicative" />
            {{ t("conversation_flagged_for_attention") }}
          </span>
        </div>
      </div>
      <overflow-menu v-if="showOverflowMenu" class="comment-actions" :flip-menu="true">
        <template #trigger>
          <svg-icon name="Kebab24" kind="action" />
        </template>
        <OverflowMenuItem v-if="showEditButton" :disabled="!editable" @click="editComment">
          <svg-icon name="Pencil24" kind="action" />
          {{ t("button_edit") }}
        </OverflowMenuItem>
        <OverflowMenuItem :disabled="!deletable" @click="deleteCommentConfirmation">
          <svg-icon name="Trash24" kind="action" />
          {{ t("button_delete") }}
        </OverflowMenuItem>
      </overflow-menu>
    </div>
    <div class="comment-content">
      <p v-if="comment.deleted" class="deleted-text">
        {{ commentDeletedMessage }}
      </p>
      <div v-else-if="isEditMode" class="comment-editor">
        <rich-text-editor
          ref="editor"
          :kind="commentListItemData.tinyMceType"
          :size="commentListItemData.textSize"
          :initial-content="commentContent"
          :question-attempt-uuid="commentQuestionAttemptUuid"
          is-mandatory
          :config="getConfig"
          @change="handleUpdateComment"
        />
        <cv-inline-notification
          v-if="hasError"
          :title="error.message"
          low-contrast
          kind="error"
          @close="handleCloseError"
        >
          <template #subtitle>
            <div v-safe-html="error.subMessage" />
          </template>
        </cv-inline-notification>
        <input-checkbox
          v-if="commentListItemData.allowNotify"
          :id="'notify_' + dirtyId"
          class="notify-checkbox"
          @change="handleSelectNotify"
        >
          {{ commentListItemData.notifyLabel || t("comment_notify_label") }}
        </input-checkbox>
        <div class="actions">
          <cv-button class="cancel" kind="secondary" size="small" @click="cancelComment"
            >{{ t("button_assignment_cancel") }}
          </cv-button>
          <fetch-data-button
            class="save"
            :kind="dataSaveButton.kind"
            :size="dataSaveButton.size"
            :loading="dataSaveButton.loading"
            :disabled="dataSaveButton.loading"
            @click="updateComment"
          >
            {{ t("button_post") }}
          </fetch-data-button>
        </div>
      </div>
      <rich-text-wrapper
        v-else
        :content="displayComment"
        :config="getConfig"
        :is-comment="true"
        :time-stamp="timeStamp"
        :expandable="expandable"
        :expanded="isLastComment"
        :truncate="truncate"
        :can-mark-copy="true"
        :comment-context="commentContext"
        :anchor="teacherFeedbackCommentAnchor"
        :allow-ask-salis="allowAskSalis"
        :can-ask-salis="canAskSalis"
        :is-ask-salis-supported-component="isAskSalisSupportedComponent"
        @mark-google-file="handleMarkGoogleFile"
        @mark-copy="handleMarkCopy"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import store from "./CommentStore";
import DateTimeFormatter from "@utils/datetime.formatter";
import CommentApi from "@apis/comment.api";
import { isEmpty } from "@utils/var.check";
import { buildTeacherFeedbackCommentAnchor } from "@modules/annotation/service/annotation.anchor.utils.ts";
import CommentEventBus from "@plugins/comment/service/event-bus";
import { APP_LANGUAGE, INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { TopicResourceEventBus } from "@plugins/topic/topic-resource.event.bus";
import appConfigs from "@configs/app";
import { AVATAR_DEFAULT_SVG_FILENAME } from "@modules/user-avatar/service/user.avatar.constant";
import { COMMENT_RESOURCE_TYPE, WCPM_REGEX } from "@plugins/comment/comment.constant.ts";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useStore } from "vuex";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import { useMention } from "@plugins/richtext/plugins/mentions/composables/mention.ts";
import { ApiResponse } from "@apis/api.ts";
import { CommentResponse } from "@plugins/comment/types.ts";
import ObjectUtils from "@utils/object.utils";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";

// Props
interface Props {
  comment: CommentResponse;
  commentListItemData: Record<string, unknown>;
  academicYear?: number;
  fromDiscussion?: boolean;
  config?: Record<string, unknown>;
  isLastComment: boolean;
  expandable?: boolean;
  truncate?: boolean;
  getMentionableTeacherList?: () => object;
  getMentionableStudentList?: () => object;
  viewedUserId?: number;
  anchor?: string;
  allowAskSalis?: boolean;
  canAskSalis?: boolean;
  isAskSalisSupportedComponent?: boolean;
}

const {
  comment,
  commentListItemData,
  academicYear = 0,
  isLastComment,
  viewedUserId = 0,
  fromDiscussion = false,
  config = {},
  expandable = true,
  truncate = false,
  getMentionableTeacherList = () => ({}),
  getMentionableStudentList = () => ({}),
  anchor = "",
  allowAskSalis = false,
  canAskSalis = false,
  isAskSalisSupportedComponent = false,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  restrictInteraction: [];
  markGoogleFile: [data: unknown];
  markCopy: [data: unknown];
}>();

const vuexStore = useStore();
const { t } = useI18n();
const modal = useModal();
const { error, actionApiFailHandler } = useErrorHandler();
const dirtyId = ref(comment.resourceUuid);
const { mentionedTeachers, mentionedStudents } = useMention();
const { setDirty, promptPageDirty, promptComponentDirty, removeDirty } = useDirty(dirtyId);

// Data
const notify = ref(false);
const commentDateString = ref(
  DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(comment.modifiedDate),
);
const hasEdit = ref(false);
const dataSaveButton = ref({
  kind: "primary",
  size: "small",
  loading: false,
});

// Template Refs
const editor = useTemplateRef<HTMLElement>("editor");

// Computed
const userId = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/userId`]);
const isTeacher = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/isTeacher`]);
const getIntegratorAppAccessByAppFeatureId = computed(
  () => vuexStore.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`],
);
const attemptContext = computed(() => vuexStore.state[NAMESPACE.ATTEMPT].attemptContext);

const cssClass = computed(() => {
  if (comment && comment.deleted) {
    return "deleted";
  }
  return "";
});

const editable = computed(() => {
  if (commentListItemData.freezeInteraction) return false;
  if (commentListItemData.ownerRestricted) {
    // cannot edit own or others comments
    if (!commentListItemData.editable) return false;

    // can edit own comment only
    return parseInt(userId.value) === comment.commentedByUserId && commentListItemData.allowComment;
  }
  return (
    commentListItemData.allowComment &&
    (parseInt(userId.value) === comment.commentedByUserId ||
      isAppCreatedCommentEditableByTeacher.value)
  );
});

const isAppCreatedCommentEditableByTeacher = computed(() => {
  // SLS-9771 Teacher can edit a comment (from LFA SA app) in teacher-student view.
  // SLS-10771 DAT quiz comment
  // SLS-10988 & SLS-10994 DAT FRQ & Quiz comment
  // SLS-11563 LTI comment
  // SLS-11626 HTML5 comment
  if (!isTeacher.value) {
    return false;
  }

  if (isCommentCreatedByLtiApp.value) {
    return true;
  }

  return (
    isCommentCreatedByIntegratorApp.value &&
    (isLfaSA.value || isDataAssistantComment.value || isHtml5Comment.value)
  );
});

const deletable = computed(() => {
  //ownerRestricted false: comment owner OR any teacher can delete (except observer)
  //1. ITT/annotation/forum (SLS-6410)
  //2. teacherFeedback (SLS-6142, SLS-7386)

  //freezeInteraction: only owner can delete(same for ITT/Annotation/teacherFeedback/forum comments)(SLS-6234)
  if (commentListItemData.freezeInteraction || commentListItemData.ownerRestricted) {
    // cannot delete own or others comments
    if (!commentListItemData.deletable) return false;

    // can delete own comment only
    return parseInt(userId.value) === comment.commentedByUserId && commentListItemData.allowComment;
  } else {
    //teachers able to delete all comments
    //observers and students only allowed to delete own comments
    return (
      ((isTeacher.value && commentListItemData.deletable) ||
        parseInt(userId.value) === comment.commentedByUserId) &&
      commentListItemData.allowComment
    );
  }
});

const edited = computed(() => {
  return comment.createdDate !== comment.modifiedDate;
});

const hasError = computed(() => {
  return error.value && error.value.message !== "";
});

const isEditMode = computed(() => {
  return store.getEditCommentId(comment.resourceUuid) === comment.id;
});

const richTextEditorConfig = computed(() => {
  return {
    fileLimit: {
      all: 1,
    },
    plugins: {
      fileUpload: {
        fileServiceConfig: {
          getContext: getContext,
          type: "ugc",
        },
      },
      drawing: {
        fileServiceConfig: {
          getContext: getContext,
          type: "ugc",
        },
      },
      google: {
        googleServiceConfig: {
          type: "google-attachment",
          googleAttachmentLimit: 10,
        },
      },
      customMention: {
        mentionConfig: {
          getMentionableTeacherList: getMentionableTeacherList,
          getMentionableStudentList: getMentionableStudentList,
        },
      },
    },
  };
});

const showOverflowMenu = computed(() => {
  return !comment.deleted && (editable.value || deletable.value);
});

const showEditButton = computed(() => {
  return !isEditMode.value && editable.value;
});

const commentContent = computed(() => {
  return store.getEditCommentContent(comment.resourceUuid);
});

const timeStamp = computed(() => {
  return store.getNewCommentTimeStamp();
});

const getConfig = computed(() => {
  // return config if is teacher feedback tinymce
  const baseConfig =
    commentListItemData.tinyMceType === "teacher_tinymce_11" ? config : richTextEditorConfig.value;

  const configToUse = ObjectUtils.deepClone(baseConfig);

  if (configToUse?.plugins?.google?.googleServiceConfig) {
    configToUse.plugins.google.googleServiceConfig.getContext = () => {
      return new Promise((resolve) => {
        resolve({
          resourceType: commentListItemData.resourceType,
          resourceUuid: commentListItemData.resourceUuid,
          resourceId: commentListItemData.resourceId,
          commentedByUserId: comment.commentedByUserId,
        });
      });
    };
  }

  return configToUse;
});

const commentedByUserId = computed(() => {
  return comment.commentedByUserId;
});

const commentedBy = computed(() => {
  if (isCommentCreatedByScorableApp.value) {
    return t("question_type_isr_label");
  }

  if (!isEmpty(comment.appFeatureId)) {
    return appFeatureName.value;
  }

  return comment.commentedBy;
});

const commentedByUserAvatarFile = computed(() => {
  let fileName = comment.avatarFilename;
  const fileVersion = comment.avatarFileVersion;

  // fallback
  if (isEmpty(fileName))
    fileName = comment.commentedByTeacher
      ? AVATAR_DEFAULT_SVG_FILENAME.NON_STUDENT
      : AVATAR_DEFAULT_SVG_FILENAME.STUDENT;

  return fileName.startsWith("default")
    ? `${appConfigs.s3ResourceDomain}/avatar/user/${fileName}.svg?t=${new Date().getTime()}`
    : `${
        appConfigs.s3ResourceDomain
      }/avatar/user/${fileName}/${fileVersion}/${fileName}.svg?t=${new Date().getTime()}`;
});

const appFeature = computed(() => {
  return getIntegratorAppAccessByAppFeatureId.value(comment.appFeatureId);
});

const hideSEWordsCorrectPerMinute = computed(() => {
  const { category, language } = appFeature.value ?? {};
  return (
    category === INTEGRATOR_APP_FEATURE_CATEGORY.SE && !!language && language !== APP_LANGUAGE.EN
  );
});

const appFeatureName = computed(() => {
  if (isLearningAssistantComment.value) {
    return t("learning_assistant_app_feature_name");
  } else if (isDataAssistantComment.value) {
    return t("data_assistant_app_feature_name");
  }
  return appFeature.value?.appFeatureName || "";
});

const isCommentCreatedByIntegratorApp = computed(() => {
  return !isEmpty(comment?.appFeatureId);
});

const isCommentCreatedByLtiApp = computed(() => {
  return !isEmpty(comment?.appId);
});

const isLfaSA = computed(() => {
  return appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.SA;
});

const isLearningAssistantComment = computed(() => {
  return (
    appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.LA_POST ||
    appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEAM ||
    appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.LA_SPEECH
  );
});

const isDataAssistantComment = computed(() => {
  return (
    appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.DA_QUIZ ||
    appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_QUIZ ||
    appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.DA_AGG_FRQ
  );
});

const isHtml5Comment = computed(() => {
  return appFeature.value?.category === INTEGRATOR_APP_FEATURE_CATEGORY.HTML5;
});

const isCommentCreatedByScorableApp = computed(() => {
  return isCommentCreatedByLtiApp.value || isHtml5Comment.value;
});

const commentContext = computed(() => {
  return {
    resourceType: commentListItemData.resourceType,
    resourceUuid: commentListItemData.resourceUuid,
  };
});

const commentQuestionAttemptUuid = computed(() => {
  return comment.resourceType === COMMENT_RESOURCE_TYPE.QUESTION_ANSWER ? comment.resourceUuid : "";
});

const commentDeletedMessage = computed(() => {
  return commentListItemData.isFeedback
    ? t("feedback_deleted_message")
    : t("comment_deleted_message");
});

const teacherFeedbackCommentAnchor = computed(() => {
  if (!isEmpty(anchor)) {
    return buildTeacherFeedbackCommentAnchor(anchor, comment.id);
  }
  return "";
});

const displayComment = computed(() => {
  if (!comment.comment) return comment.comment;

  if (hideSEWordsCorrectPerMinute.value) {
    return comment.comment.replace(WCPM_REGEX, "");
  }
  return comment.comment;
});

const showFlaggedMessage = computed(() => {
  return comment.flagged && isTeacher.value;
});

// Methods
const editComment = () => {
  const prompt =
    commentListItemData.resourceType == COMMENT_RESOURCE_TYPE.ANNOTATION
      ? promptPageDirty
      : promptComponentDirty;
  prompt().then((response) => {
    if (!response) {
      return;
    }
    store.setEditComment(comment.resourceUuid, comment.id, comment.comment);
  });
};

const updateComment = () => {
  if (!editor.value.validate()) return;
  const requestData = {
    rt_comment: commentContent.value,
    uuid: comment.uuid,
    notify: notify.value,
    resourceType: comment.resourceType,
    resourceUuid: comment.resourceUuid,
    academicYear: academicYear,
    attemptableUuid: fromDiscussion ? attemptContext.value?.attemptableUuid : null,
    attemptableType: fromDiscussion ? attemptContext.value?.attemptableType : null,
    attemptType: fromDiscussion ? attemptContext.value?.attemptType : null,
    mentionModel: {
      mentionedTeachers: mentionedTeachers.value,
      mentionedStudents: mentionedStudents.value,
      viewedUserId: viewedUserId,
    },
  };
  dataSaveButton.value.loading = true;
  CommentApi.editComment(requestData).then(
    (response: ApiResponse<CommentResponse>) => {
      //OK
      if (response.code === "error") {
        dataSaveButton.value.loading = false;
        return handleErrorEvent(response.errorMap, true);
      }
      if (response.data) {
        const updatedComment = response.data;

        commentDateString.value = DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(
          updatedComment.modifiedDate,
        );
        CommentEventBus.$emit("updateTimeStamp", {});
        store.updateLastCommentInfo(
          updatedComment.resourceUuid,
          updatedComment.resourceType,
          updatedComment,
        );
      }
      removeDirty(dirtyId.value);
      store.remove(comment.resourceUuid);

      // refetch comment
      CommentEventBus.$emit("commentDataRefetch", {});

      hasEdit.value = true;

      if (fromDiscussion) {
        TopicResourceEventBus.$emit("discussionAttempted", {});
      }

      dataSaveButton.value.loading = false;
    },
    () => {
      dataSaveButton.value.loading = false;
      actionApiFailHandler();
    },
  );
};

const cancelComment = () => {
  promptComponentDirty().then((response) => {
    if (response) {
      const requestData = {
        rt_comment: commentContent.value,
        uuid: comment.uuid,
        notify: notify.value,
        resourceType: comment.resourceType,
        resourceUuid: comment.resourceUuid,
        academicYear: academicYear,
      };
      // reset google document permission
      CommentApi.clearPermissionQuestionAnswerGoogleDocumentComment(requestData).then(
        (newResponse: ApiResponse<unknown>) => {
          if (newResponse.code !== "ok") return;

          store.remove(comment.resourceUuid);
          clearErrorState();
        },
      );
    }
  });
};

const deleteCommentConfirmation = () => {
  const modalParams = {
    title: commentListItemData.isFeedback
      ? t("feedback_deleted_modal_title")
      : t("comment_deleted_modal_title"),
    content: commentListItemData.isFeedback
      ? t("feedback_deleted_modal_message")
      : t("comment_deleted_modal_message"),
    primaryBtnLabel: t("button_assignment_ok"),
    secondaryBtnLabel: t("button_assignment_cancel"),
    actionPrimary: deleteComment,
  };
  modal.showMessage("warning", modalParams);
};

const deleteComment = () => {
  const requestData = {
    uuid: comment.uuid,
    resourceType: comment.resourceType,
    resourceUuid: comment.resourceUuid,
    academicYear: academicYear,
    attemptableUuid: fromDiscussion ? attemptContext.value?.attemptableUuid : null,
    attemptableType: fromDiscussion ? attemptContext.value?.attemptableType : null,
    attemptType: fromDiscussion ? attemptContext.value?.attemptType : null,
  };

  CommentApi.softDeleteComment(requestData).then((response: ApiResponse<CommentResponse>) => {
    //OK
    if (response.code === "error") {
      dataSaveButton.value.loading = false;
      return handleErrorEvent(response.errorMap, true);
    }
    if (response.data) {
      // refetch comment
      CommentEventBus.$emit("commentDataRefetch", {});

      if (fromDiscussion) {
        TopicResourceEventBus.$emit("discussionAttempted", {});
      }
      store.deletedLastComment(requestData);
    }
  }, actionApiFailHandler);
};

const handleUpdateComment = (value) => {
  setDirty(dirtyId.value);
  store.setEditComment(comment.resourceUuid, comment.id, value);
};

const handleCloseError = () => {
  clearErrorState();
};

const clearErrorState = () => {
  error.value.message = "";
  error.value.subMessage = "";
};

const handleErrorEvent = (errorMap, action) => {
  let content = "";
  const title = t("comment_title_label");
  let actionPrimary, actionSecondary, actionHideRequest;
  const errorType = errorMap[0];

  switch (errorType) {
    case "CommentResourceAccessDenied":
      content = t("comment_resource_access_denied_label");
      actionPrimary = restrictInteraction;
      actionSecondary = restrictInteraction;
      actionHideRequest = restrictInteraction;
      break;
    case "CommentResourceNotFound":
      content = t("comment_resource_not_found_label");
      actionPrimary = restrictInteraction;
      actionSecondary = restrictInteraction;
      actionHideRequest = restrictInteraction;
      break;
    case "CommentDeleted":
      content = t("comment_deleted_label");
      break;
    default:
      if (action) {
        actionApiFailHandler();
      }
      return;
  }
  const modalParams = {
    title: title,
    content: content,
    primaryBtnLabel: t("button_ok"),
    secondaryBtnLabel: "CANCEL",
    actionPrimary: actionPrimary,
    actionSecondary: actionSecondary,
    actionHideRequest: actionHideRequest,
  };
  modal.showMessage("warning", modalParams);
};

const restrictInteraction = () => {
  emit("restrictInteraction");
};

const getContext = () => {
  return new Promise((resolve) => {
    resolve({
      resourceType: "COMMENT",
      resourceUuid: comment.uuid,
      resourceId: comment.id,
    });
  });
};

const handleSelectNotify = (checked) => {
  notify.value = checked.length > 0;
};

const handleMarkGoogleFile = (data) => {
  emit("markGoogleFile", data);
};

const handleMarkCopy = (data) => {
  emit("markCopy", data);
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/abstracts/mixins";

.comment-list-item {
  position: relative;
  padding: $spacing-05 $spacing-05 $spacing-05 0;

  .comment-actions {
    position: absolute;
    right: 0;
    top: $spacing-03;
  }

  .comment-content {
    @include carbon--type-style("body-short-01", true);
    @include media-hover();
    margin-top: $spacing-03;
    word-break: break-word;

    .actions {
      margin-top: $spacing-05;
      display: flex;
      justify-content: center;
    }

    .deleted-text {
      font-size: small;
      font-style: italic;
    }

    .fetch-data-button.save {
      display: flex;
      margin-left: $spacing-05;
    }

    .input-checkbox {
      > label {
        .text {
          @include carbon--type-style("body-short-01");
        }
      }
    }
  }

  .comment-metadata {
    display: flex;
    flex-flow: column nowrap;
    padding-right: $spacing-05;
  }

  .comment-avatar-name {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: $spacing-03;
    margin-bottom: $spacing-02;

    > .user-avatar {
      width: 24px;
      height: 24px;
    }
  }

  .edited {
    margin-right: $spacing-03;
    font-style: italic;
  }

  .btn-mark-copy {
    &:first-child {
      margin-left: auto;
    }
  }

  .info {
    @include metadata-separator();
    @include carbon--type-style("card-label-01", true);
    color: $text-02;
    display: flex;
    align-items: center;
    flex: 0 0 100%;
  }

  .flagged-text {
    color: $text-01;
  }
  .sls-icon {
    margin-right: $spacing-02;
    [fill] {
      fill: #fbbc04;
    }
    [fill="#DFE8F9"] {
      fill: #fef2cd;
    }
  }

  .name {
    @include carbon--type-style("card-title-01", true);
    padding-right: $spacing-05;

    &.learning-fa {
      color: $ai;
    }
  }

  .attachment-placeholder {
    background-color: $ui-background;

    // SLSUAT-3306 - Override styling as this is nested in comment list item
    // so that the media/google documents have the same type token
    .title {
      @include carbon--type-style("body-long-01", true);
    }
  }
}
</style>
